import Card from '@/classes/card';
import { formatCompletionTime, stripHtml } from '@/utils';

export default class Deck {
  constructor(deck) {
    this.cards = deck.cards.map(c => new Card(c));
    this.deckType = deck.deckType;
    this.iconId = deck.iconId;
    this.id = deck.id;
    this.tags = deck.tags;
    this.title = deck.title;
    this.contentSourceId = deck.contentSourceId;
    this.createdAt = deck.createdAt;
    this.difficultyLowerBound = deck.difficultyLowerBound;
    this.difficultyUpperBound = deck.difficultyUpperBound;
    this.displayAttachmentId = deck.displayAttachmentId;
    this.displayDescription = deck.displayDescription;
    this.displayTitle = deck.displayTitle;
  }


  get libraryRoute() {
    return {
      name: 'library#card',
      params: {
        deckId: this.id,
      },
    };
  }

  get listRowType() {
    return 'deck-list-row';
  }

  get assetType() {
    return 'Deck';
  }

  get isSeriesDeck() {
    return this.deckType === 'Episodes' || this.decktype === 'Duration';
  }

  get assetDescription() {
    if (this.displayDescription) {
      return stripHtml(this.displayDescription);
    }
  }

  get assetTitle() {
    let title = this.displayTitle || this.title || 'Deck';
    return stripHtml(title);
  }

  get icon() {
    return 'series';
  }

  completedState(states, store) {
    if (this.isSeriesDeck) {
      if (states.length !== this.cards.length) {
        return false;
      }

      return states.every(s => {
        const attachment = store.getters['attachments/get'][s.attachmentId];
        if (attachment) {
          return attachment.completedState(s);
        }
        // default to false if no attachment was found. shouldn't ever be a thing but let's be safe.
        return false;
      });
    }
  }

  inProgressState(states) {
    if (this.isSeriesDeck) {
      return Array.isArray(states) && states.length > 0;
    }
  }

  cardMetadata(store) {
    const plural = this.cards.length === 1 ? '' : 's';
    if (this.deckType === 'Episodes') {
      return `${this.cards.length} Episode${plural}`;
    } else if (this.deckType === 'Duration') {
      const assets = this.cards.map(c => store.getters[c.getter][c.assetId]);
      const totalTime = assets.reduce((totalTime, asset) => {
        if (asset && asset.completionTime) {
          totalTime += asset.completionTime;
        }
        return totalTime;
      }, 0);
      return formatCompletionTime(totalTime);
    } else {
      return `${this.cards.length} Article${plural}`;
    }
  }

  fetchAssets(store) {
    let promises = [];
    const { attachmentIds, topicIds, learningModuleIds } = this.gatherAssetIds();
    if (this.displayAttachmentId) {
      attachmentIds.push(this.displayAttachmentId);
    }
    if (topicIds.length > 0) {
      promises.push(store.dispatch('topics/load', { ids: topicIds }));
    }
    if (learningModuleIds.length > 0) {
      promises.push(store.dispatch('learningModules/fetchAll', { ids: learningModuleIds }));
    }
    if (attachmentIds.length > 0) {
      promises.push(store.dispatch('attachments/fetch', { ids: attachmentIds }));
    }
    return Promise.all(promises);
  }

  gatherAssetIds() {
    const attachmentIds = [];
    const topicIds = [];
    const learningModuleIds = [];
    for (const card of (this.cards || [])) {
      if (card.featuredImageId) {
        attachmentIds.push(card.featuredImageId);
      }
      if (card.isTopicCard) {
        topicIds.push(card.assetId);
      }
      if (card.isAttachmentCard) {
        attachmentIds.push(card.assetId);
      }
      if (card.isLearningModuleCard) {
        learningModuleIds.push(card.assetId);
      }
    }
    return { attachmentIds, topicIds, learningModuleIds };
  }

  difficultyLabel(store) {
    if (this.difficultyLowerBound) {
      const allLevels = store.getters['difficultyLevels/all'];
      const level = allLevels.find(l => l.difficultyLevel == this.difficultyLowerBound);
      return level?.label;
    }
  }

  states(store) {
    if (this.isSeriesDeck) {
      const seriesAttachmentIds = this.cards.filter(card => card.isAttachmentCard).map(card => card.assetId);
      return seriesAttachmentIds.map(id => store.getters['attachmentStates/get'][id]).filter(s => s);
    }
  }
}
